import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Navigation, ArrowRight } from 'lucide-react';

// Nearby / alternate airports per hub. `guide: true` links to the full guide
// page; otherwise deep-links into the predictor with the airport preselected.
const NEARBY = {
  ATL: [
    { code: 'CLT', name: 'Charlotte Douglas', note: 'Alternate Southeast hub, ~4h drive', guide: true },
    { code: 'BNA', name: 'Nashville Intl', note: 'Alternate for north Georgia/Tennessee travelers' },
    { code: 'SAV', name: 'Savannah/Hilton Head', note: 'Coastal Georgia alternative' },
  ],
  LAX: [
    { code: 'SNA', name: 'John Wayne (Orange County)', note: '~40 mi south, far calmer security' },
    { code: 'BUR', name: 'Hollywood Burbank', note: 'Fastest curb-to-gate in LA basin' },
    { code: 'ONT', name: 'Ontario Intl', note: 'Inland Empire alternative' },
    { code: 'SAN', name: 'San Diego Intl', note: '~120 mi south', guide: true },
  ],
  ORD: [
    { code: 'MDW', name: 'Chicago Midway', note: 'Southwest hub, often shorter lines' },
    { code: 'MKE', name: 'Milwaukee Mitchell', note: '~75 mi north, low-stress alternative' },
  ],
  DFW: [
    { code: 'DAL', name: 'Dallas Love Field', note: 'Southwest hub, closer to downtown' },
    { code: 'IAH', name: 'Houston Bush', note: 'Alternate Texas megahub', guide: true },
    { code: 'AUS', name: 'Austin-Bergstrom', note: '~3h south' },
  ],
  DEN: [
    { code: 'COS', name: 'Colorado Springs', note: '~70 mi south, tiny security lines' },
  ],
  JFK: [
    { code: 'LGA', name: 'LaGuardia', note: 'Closer to Manhattan for domestic trips' },
    { code: 'EWR', name: 'Newark Liberty', note: 'Best for New Jersey and west Manhattan', guide: true },
    { code: 'HPN', name: 'Westchester County', note: 'Calm alternative north of the city' },
  ],
  SFO: [
    { code: 'OAK', name: 'Oakland Intl', note: 'Across the bay, fewer fog delays' },
    { code: 'SJC', name: 'San Jose Mineta', note: 'Best for South Bay travelers' },
  ],
  SEA: [
    { code: 'PDX', name: 'Portland Intl', note: '~3h south' },
    { code: 'GEG', name: 'Spokane Intl', note: 'Eastern Washington alternative' },
  ],
  LAS: [
    { code: 'PHX', name: 'Phoenix Sky Harbor', note: 'Alternate Southwest hub', guide: true },
    { code: 'LAX', name: 'Los Angeles Intl', note: '~4h drive', guide: true },
  ],
  MCO: [
    { code: 'TPA', name: 'Tampa Intl', note: '~85 mi west, consistently calmer' },
    { code: 'MIA', name: 'Miami Intl', note: 'South Florida gateway', guide: true },
    { code: 'JAX', name: 'Jacksonville Intl', note: 'Northeast Florida alternative' },
  ],
  MIA: [
    { code: 'FLL', name: 'Fort Lauderdale-Hollywood', note: '~25 mi north, cheaper fares', guide: true },
    { code: 'PBI', name: 'Palm Beach Intl', note: 'Calmest of the three South Florida airports' },
    { code: 'MCO', name: 'Orlando Intl', note: 'Central Florida option', guide: true },
  ],
  EWR: [
    { code: 'JFK', name: 'John F. Kennedy', note: 'More international options', guide: true },
    { code: 'LGA', name: 'LaGuardia', note: 'Domestic alternative in Queens' },
    { code: 'PHL', name: 'Philadelphia Intl', note: '~80 mi south', guide: true },
  ],
  BOS: [
    { code: 'PVD', name: 'Providence T.F. Green', note: '~60 mi south, quick security' },
    { code: 'MHT', name: 'Manchester-Boston', note: 'New Hampshire alternative' },
    { code: 'BDL', name: 'Hartford Bradley', note: 'Western New England option' },
  ],
  CLT: [
    { code: 'RDU', name: 'Raleigh-Durham', note: '~2.5h northeast' },
    { code: 'GSP', name: 'Greenville-Spartanburg', note: 'Upstate SC, minimal lines' },
    { code: 'ATL', name: 'Hartsfield-Jackson Atlanta', note: 'Megahub alternative', guide: true },
  ],
  PHX: [
    { code: 'TUS', name: 'Tucson Intl', note: '~115 mi southeast, tiny waits' },
    { code: 'LAS', name: 'Harry Reid Las Vegas', note: 'Alternate Southwest-region hub', guide: true },
  ],
  IAH: [
    { code: 'HOU', name: 'Houston Hobby', note: 'Southwest hub, closer to downtown' },
    { code: 'AUS', name: 'Austin-Bergstrom', note: '~2.5h west' },
    { code: 'DFW', name: 'Dallas/Fort Worth', note: 'Alternate Texas megahub', guide: true },
  ],
  SEA_DEFAULT: [],
};

export default function NearbyAirports({ code }) {
  const items = NEARBY[(code || '').toUpperCase()];
  if (!items || items.length === 0) return null;

  return (
    <Card className="border-slate-200 bg-white mt-8" data-testid="nearby-airports">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-1">
          <Navigation className="w-4 h-4 text-blue-500" />
          <h2 className="heading text-base sm:text-lg font-semibold text-slate-900">
            Nearby &amp; alternate airports
          </h2>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          When {code?.toUpperCase()} looks slammed, these alternatives are worth comparing before you book.
        </p>
        <ul className="divide-y divide-slate-100">
          {items.map((a) => {
            const to = a.guide ? `/airports/${a.code.toLowerCase()}` : `/?airport=${a.code}`;
            return (
              <li key={a.code}>
                <Link
                  to={to}
                  data-testid={`nearby-airport-${a.code}`}
                  className="group flex items-center justify-between gap-3 py-2.5 hover:bg-slate-50 -mx-2 px-2 rounded"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="font-mono font-semibold text-slate-900 w-10">{a.code}</span>
                    <div className="min-w-0">
                      <span className="text-sm text-slate-700">{a.name}</span>
                      <span className="hidden sm:inline text-xs text-slate-400"> — {a.note}</span>
                    </div>
                  </div>
                  <span className="flex items-center gap-1 text-xs text-blue-600 whitespace-nowrap">
                    {a.guide ? 'Guide' : 'Check crowds'}
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
